/**
 * Web stand-in for `react-native-print`.
 *
 * Only loaded in the web bundle (wired up through resolve.alias in
 * webpack.config.js). Android/iOS keep using the real native module.
 *
 * The native API used by this app is:
 *   RNPrint.print({html, isLandscape, printerURL})
 *   RNPrint.selectPrinter({x, y})
 *
 * In a browser we render the receipt HTML into an off-screen iframe and call
 * `window.print()` on it. Using an iframe (rather than printing the document)
 * keeps the POS UI off the printed page and lets the receipt bring its own
 * stylesheet and @page size.
 */

export type PrintOptions = {
  html?: string;
  filePath?: string;
  isLandscape?: boolean;
  printerURL?: string;
  jobName?: string;
};

export type SelectPrinterOptions = {
  x?: number | string;
  y?: number | string;
};

export type SelectedPrinter = {
  url: string;
  name: string;
};

const IFRAME_ID = 'zenbasket-pos-print-frame';

function removeFrame(frame: HTMLIFrameElement): void {
  // Give the print dialog time to take its snapshot before tearing down.
  window.setTimeout(() => {
    if (frame.parentNode) {
      frame.parentNode.removeChild(frame);
    }
  }, 1000);
}

function printHtml(html: string, isLandscape: boolean): Promise<void> {
  return new Promise((resolve, reject) => {
    if (typeof document === 'undefined') {
      reject(new Error('Printing is only available in a browser.'));
      return;
    }

    const existing = document.getElementById(IFRAME_ID);
    if (existing && existing.parentNode) {
      existing.parentNode.removeChild(existing);
    }

    const frame = document.createElement('iframe');
    frame.id = IFRAME_ID;
    frame.setAttribute('aria-hidden', 'true');
    // Off-screen rather than display:none - some browsers refuse to print a
    // frame that was never laid out.
    frame.style.position = 'fixed';
    frame.style.right = '0';
    frame.style.bottom = '0';
    frame.style.width = '0';
    frame.style.height = '0';
    frame.style.border = '0';
    /*
     * The receipt interpolates customer, product and store text without
     * escaping. No `allow-scripts`, so markup in that data cannot run in the
     * POS origin (where the login token lives). allow-same-origin lets us
     * reach the document to add styles and call print(); allow-modals is what
     * print() needs inside a sandbox.
     */
    frame.setAttribute('sandbox', 'allow-same-origin allow-modals');

    let printed = false;

    frame.onload = () => {
      // srcdoc fires load once, with the receipt in place - but stay safe.
      if (printed) {
        return;
      }
      const frameWindow = frame.contentWindow;
      const frameDocument = frame.contentDocument || (frameWindow && frameWindow.document);

      if (!frameWindow || !frameDocument) {
        removeFrame(frame);
        reject(new Error('Unable to open the print preview.'));
        return;
      }

      // Receipt rolls are portrait and narrow; the caller can request landscape.
      const pageStyle = frameDocument.createElement('style');
      pageStyle.appendChild(
        frameDocument.createTextNode(
          `@page { size: ${isLandscape ? 'landscape' : 'portrait'}; margin: 4mm; }
           body { margin: 0; }`,
        ),
      );
      // First in <head>, so an @page rule the receipt brings (printer.web.ts
      // sets 80mm roll paper) comes later and wins.
      frameDocument.head.insertBefore(pageStyle, frameDocument.head.firstChild);

      // Wait for remote logos/icons in the receipt so they are not blank.
      const done = () => {
        if (printed) {
          return;
        }
        printed = true;
        try {
          frameWindow.focus();
          frameWindow.print();
          resolve();
        } catch (error) {
          reject(error as Error);
        } finally {
          removeFrame(frame);
        }
      };

      const images = Array.from(frameDocument.images || []);
      const pending = images.filter(img => !img.complete);

      if (pending.length === 0) {
        done();
        return;
      }

      let settled = 0;
      const onSettle = () => {
        settled += 1;
        if (settled >= pending.length) {
          done();
        }
      };
      pending.forEach(img => {
        img.addEventListener('load', onSettle, {once: true});
        img.addEventListener('error', onSettle, {once: true});
      });
      // Never block the cashier on a slow CDN.
      window.setTimeout(() => {
        if (settled < pending.length) {
          settled = pending.length;
          done();
        }
      }, 3000);
    };

    /*
     * srcdoc, set before insertion: `load` then fires once, after the receipt
     * is parsed. Writing into the frame after appendChild (the old approach)
     * fired `load` on the empty about:blank document first - a blank print
     * dialog, followed by a second one with the receipt.
     */
    frame.srcdoc = html;
    document.body.appendChild(frame);
  });
}

const RNPrint = {
  async print(options: PrintOptions): Promise<void> {
    if (options.html) {
      return printHtml(options.html, Boolean(options.isLandscape));
    }
    if (options.filePath) {
      // A PDF/other file URL - hand it to the browser's own viewer.
      window.open(options.filePath, '_blank', 'noopener');
      return;
    }
    throw new Error('RNPrint.print (web) requires an `html` or `filePath` option.');
  },

  /**
   * The browser owns printer selection inside its own print dialog, so there
   * is nothing to show here. Resolving with `null` keeps callers that do
   * `await RNPrint.selectPrinter()` working unchanged.
   */
  async selectPrinter(_options?: SelectPrinterOptions): Promise<SelectedPrinter | null> {
    return null;
  },
};

export default RNPrint;
